import type { ExcuseKind, ExcuseNoteDraft, ExcuseNoteView, NoteValues } from "@vaulted/shared";
import { EXCUSE_KINDS, EXCUSE_KIND_LABELS, fillNote, noteDate, noteTemplate } from "@vaulted/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { errorMessage, get, post } from "../api";
import { TextNumberSelect, useTextNumbers } from "./TextNumberSelect";
import { SignatureInput } from "./SignatureInput";
import { Alert, Button, Checkbox, Dialog, Field, Loading, Panel, PanelHeader, TextAreaField, cx } from "./ui";


/** Write and sign a school or work note for a visit, then print, text, or email it. */
export function NoteDialog({ appointmentId, clientId, onClose }: { appointmentId: string; clientId: string; onClose: () => void }) {
  const qc = useQueryClient();
  const draft = useQuery({ queryKey: ["note-draft", appointmentId], queryFn: () => get<ExcuseNoteDraft>(`/api/appointments/${appointmentId}/note-draft`), staleTime: 0 });
  const [saved, setSaved] = useState<ExcuseNoteView | null>(null);
  return (
    <Dialog title={saved ? `${EXCUSE_KIND_LABELS[saved.kind]} saved` : "School / work note"} onClose={onClose} wide>
      {draft.isLoading ? (
        <Loading />
      ) : draft.error || !draft.data ? (
        <Alert>{errorMessage(draft.error)}</Alert>
      ) : saved ? (
        <NoteDelivery note={saved} clientId={clientId} onDone={onClose} />
      ) : (
        <NoteForm
          d={draft.data}
          appointmentId={appointmentId}
          onSaved={async (n) => {
            setSaved(n);
            await qc.invalidateQueries({ queryKey: ["notes", clientId] });
            await qc.invalidateQueries({ queryKey: ["my-signature"] });
          }}
        />
      )}
    </Dialog>
  );
}

function NoteForm({ d, appointmentId, onSaved }: { d: ExcuseNoteDraft; appointmentId: string; onSaved: (n: ExcuseNoteView) => void }) {
  const valuesFor = (ret: string): NoteValues => ({ ...d.values, "[Return date]": noteDate(ret) });
  const [kind, setKind] = useState<ExcuseKind>("school");
  const [returnDate, setReturnDate] = useState(d.defaultReturnDate);
  const [body, setBody] = useState(() => fillNote(d.templates.school, valuesFor(d.defaultReturnDate)));
  const [signature, setSignature] = useState<string | null>(null);
  const [keep, setKeep] = useState(true);
  const save = useMutation({
    mutationFn: () => post<ExcuseNoteView>(`/api/appointments/${appointmentId}/notes`, { kind, returnDate, body, ...(d.signer.hasSignature ? {} : { signature, saveSignature: keep }) }),
    onSuccess: onSaved,
  });
  // A new return date keeps any edits: the wording goes back to placeholders, then fills in again.
  const changeReturn = (next: string) => {
    if (!next || next <= d.visitDate) return;
    setBody(fillNote(noteTemplate(body, valuesFor(returnDate)), valuesFor(next)));
    setReturnDate(next);
  };
  const changeKind = (k: ExcuseKind) => {
    setKind(k);
    setBody(fillNote(d.templates[k], valuesFor(returnDate)));
  };
  return (
    <form
      className="flex flex-col gap-4"
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate();
      }}
    >
      <div className="flex flex-wrap items-end gap-4">
        <div className="flex flex-col gap-1.5">
          <span className="text-sm font-semibold text-ink-soft">Type</span>
          <div className="flex gap-1" role="group" aria-label="Type">
            {EXCUSE_KINDS.map((k) => (
              <button
                key={k}
                type="button"
                aria-pressed={kind === k}
                onClick={() => changeKind(k)}
                className={cx("rounded-[var(--radius-control)] border px-3 py-1.5 text-sm font-semibold", kind === k ? "border-brand bg-brand-wash text-brand-deep" : "border-line text-ink-soft hover:border-brand")}
              >
                {k === "school" ? "School" : "Work"}
              </button>
            ))}
          </div>
        </div>
        <Field label="Date of visit" value={noteDate(d.visitDate)} disabled className="w-44" />
        <Field label="May return on" type="date" className="w-44" min={d.defaultReturnDate} value={returnDate} onChange={(e) => changeReturn(e.target.value)} />
      </div>
      <TextAreaField label="Note" rows={7} value={body} onChange={(e) => setBody(e.target.value)} hint={`Starts from the last ${kind} note's wording, with this client's name and dates. Change anything; the next note starts from yours.`} />
      <div className="rounded-[var(--radius-control)] border border-line px-4 py-3">
        <div className="text-sm text-ink-soft">
          Signed by <span className="font-semibold text-ink">{[d.signer.name, d.signer.credentials].filter(Boolean).join(", ")}</span>
          {!d.signer.credentials && <span className="text-muted"> (add credentials like "RN BSN" on your staff page)</span>}
        </div>
        {!d.signer.hasSignature && (
          <div className="mt-3 flex flex-col gap-2">
            <SignatureInput onChange={setSignature} />
            <Checkbox label="Save my signature for next time" checked={keep} onChange={setKeep} />
          </div>
        )}
      </div>
      {save.error && <Alert>{errorMessage(save.error)}</Alert>}
      <div className="flex justify-end">
        <Button type="submit" variant="primary" busy={save.isPending} disabled={!body.trim() || (!d.signer.hasSignature && !signature)}>
          Sign and save
        </Button>
      </div>
      <p className="text-right text-xs text-muted">Saved notes can't be changed. To fix one, write a new one.</p>
    </form>
  );
}

/** Print it, or text or email a private link (opened with the client's date of birth). */
export function NoteDelivery({ note, clientId, onDone }: { note: ExcuseNoteView; clientId: string; onDone?: () => void }) {
  const qc = useQueryClient();
  const numbers = useTextNumbers(clientId);
  const [to, setTo] = useState("");
  const [sent, setSent] = useState<string | null>(null);
  const send = useMutation({
    mutationFn: (channel: "sms" | "email") => post(`/api/notes/${note.id}/send`, { channel, to: channel === "sms" ? to || null : null }),
    onSuccess: (_r, channel) => {
      setSent(channel === "sms" ? "Texted." : "Emailed.");
      void qc.invalidateQueries({ queryKey: ["client-messages", clientId] });
    },
  });
  const pdf = `/api/notes/${note.id}/pdf`;
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap gap-2">
        <Button variant="primary" onClick={() => window.open(pdf, "_blank", "noopener")}>
          Open to print
        </Button>
        <Button onClick={() => window.open(`${pdf}?download=1`, "_blank", "noopener")}>Download PDF</Button>
      </div>
      <div className="flex flex-col gap-2 rounded-[var(--radius-control)] border border-line px-4 py-3">
        <div className="text-sm text-ink-soft">Send a private link. It opens with the client's date of birth and works for 30 days.</div>
        <div className="flex flex-wrap items-end gap-2">
          <TextNumberSelect numbers={numbers.data} value={to} onChange={setTo} />
          <Button busy={send.isPending && send.variables === "sms"} disabled={send.isPending} onClick={() => send.mutate("sms")}>
            Text it
          </Button>
          <Button busy={send.isPending && send.variables === "email"} disabled={send.isPending} onClick={() => send.mutate("email")}>
            Email it
          </Button>
        </div>
        {send.error && <Alert>{errorMessage(send.error)}</Alert>}
        {sent && <Alert tone="ok">{sent}</Alert>}
      </div>
      {onDone && (
        <div className="flex justify-end">
          <Button variant="quiet" onClick={onDone}>
            Done
          </Button>
        </div>
      )}
    </div>
  );
}

/** A client's school and work notes (all, or one visit's), each with Open / Send. */
export function ExcuseNotesList({ clientId, appointmentId, title = "School and work notes" }: { clientId: string; appointmentId?: string; title?: string }) {
  const notes = useQuery({ queryKey: ["notes", clientId], queryFn: () => get<ExcuseNoteView[]>(`/api/clients/${clientId}/notes`) });
  const [open, setOpen] = useState<ExcuseNoteView | null>(null);
  const list = (notes.data ?? []).filter((n) => !appointmentId || n.appointmentId === appointmentId);
  if (appointmentId && !list.length) return null;
  return (
    <Panel>
      <PanelHeader title={title} description={appointmentId ? undefined : "Written from the visit screen. Kept as signed."} />
      {notes.isLoading ? (
        <Loading />
      ) : (
        <ul className="divide-y divide-mist">
          {!list.length && <li className="px-5 py-4 text-sm text-muted">None yet.</li>}
          {list.map((n) => (
            <li key={n.id} className="flex flex-wrap items-center justify-between gap-3 px-5 py-3">
              <div>
                <div className="font-semibold">
                  {EXCUSE_KIND_LABELS[n.kind]} · visit {noteDate(n.visitDate)}
                </div>
                <div className="text-sm text-muted">
                  {n.returnDate ? `May return ${noteDate(n.returnDate)} · ` : ""}Signed by {n.signerName} {new Date(n.signedAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                </div>
              </div>
              <Button variant="secondary" onClick={() => setOpen(n)}>
                Open / send
              </Button>
            </li>
          ))}
        </ul>
      )}
      {open && (
        <Dialog title={`${EXCUSE_KIND_LABELS[open.kind]}, visit ${noteDate(open.visitDate)}`} onClose={() => setOpen(null)} wide>
          <div className="mb-4 whitespace-pre-wrap rounded-[var(--radius-control)] bg-mist/50 px-4 py-3 text-sm">{open.body}</div>
          <NoteDelivery note={open} clientId={clientId} />
        </Dialog>
      )}
    </Panel>
  );
}

